import { createFileRoute, Link } from "@tanstack/react-router";
import { useGoals, useSubscription, formatBRL } from "@/lib/store";
import { useAuthCtx } from "@/lib/auth-context";
import { MetasSkeleton } from "@/components/skeletons";
import { useState } from "react";
import { Plus, Minus, Target, Trash2, Lock, Crown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/_app/metas")({
  head: () => ({
    meta: [
      { title: "Metas — Seu Bolso Tranquilo" },
      { name: "description", content: "Defina e acompanhe metas financeiras: reserva de emergência, viagem, compra grande e mais — com progresso em tempo real." },
      { property: "og:title", content: "Metas — Seu Bolso Tranquilo" },
      { property: "og:description", content: "Defina metas financeiras e acompanhe seu progresso mês a mês." },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://seubolsotranquilo.com/metas" }],
  }),
  component: MetasPage,
  pendingComponent: MetasSkeleton,
});

function MetasPage() {
  const { user } = useAuthCtx();
  const { goals, addGoal, updateGoal, removeGoal, loading } = useGoals(user?.id);
  const { plan, isPremium, goalsLimit } = useSubscription(user?.id);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [current, setCurrent] = useState("");
  const [goalAmounts, setGoalAmounts] = useState<Record<string, string>>({});
  const [updatingGoalId, setUpdatingGoalId] = useState<string | null>(null);

  const reachedLimit = !isPremium && goals.length >= goalsLimit;
  const limitLabel = isPremium ? "∞" : goalsLimit;

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reachedLimit) {
      toast.error(`Limite de ${goalsLimit} metas atingido`, {
        description: "Faça upgrade para o Premium para criar metas ilimitadas.",
      });
      return;
    }
    const t = parseFloat(target.replace(",", "."));
    const c = parseFloat(current.replace(",", ".")) || 0;
    if (!name || !t) return;
    const res = await addGoal({ name, target: t, current: c });
    if (res?.error) {
      const msg = res.error.message?.includes("GOALS_LIMIT_REACHED")
        ? "Você atingiu o limite de 3 metas do plano gratuito."
        : "Não foi possível criar a meta. Tente novamente.";
      toast.error(msg, {
        description: res.error.message?.includes("GOALS_LIMIT_REACHED")
          ? "Em breve, o plano Premium permitirá metas ilimitadas."
          : undefined,
      });
      return;
    }
    setName("");
    setTarget("");
    setCurrent("");
    setOpen(false);
  };

  const handleGoalAmount = async (goalId: string, currentValue: number, operation: "add" | "remove") => {
    const amount = Number(goalAmounts[goalId]?.replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Informe um valor maior que zero.");
      return;
    }
    if (operation === "remove" && amount > currentValue) {
      toast.error("O valor retirado não pode ser maior que o saldo da meta.");
      return;
    }

    const nextValue = Math.round((operation === "add" ? currentValue + amount : currentValue - amount) * 100) / 100;
    setUpdatingGoalId(goalId);
    const result = await updateGoal(goalId, { current: nextValue });
    setUpdatingGoalId(null);

    if (result.error) {
      toast.error("Não foi possível atualizar a meta. Tente novamente.");
      return;
    }
    setGoalAmounts((values) => ({ ...values, [goalId]: "" }));
    toast.success(operation === "add" ? "Valor adicionado à meta." : "Valor retirado da meta.");
  };

  if (loading) return <MetasSkeleton />;

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6 animate-in fade-in duration-300">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold">Metas financeiras</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Defina objetivos claros e acompanhe seu progresso.{" "}
            <span className="text-primary-glow font-medium">
              {goals.length}/{limitLabel} metas usadas
            </span>
          </p>
        </div>
        <button
          onClick={() => {
            if (reachedLimit) {
              toast.error(`Limite de ${goalsLimit} metas atingido`, {
                description: "Faça upgrade para o Premium para criar metas ilimitadas.",
              });
              return;
            }
            setOpen((v) => !v);
          }}
          disabled={reachedLimit}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-primary text-primary-foreground font-medium text-sm shadow-glow hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          title={reachedLimit ? "Limite do plano gratuito atingido" : "Criar nova meta"}
        >
          {reachedLimit ? <Lock className="size-4" /> : <Plus className="size-4" />}
          {reachedLimit ? "Limite atingido" : "Nova meta"}
        </button>
      </header>

      {reachedLimit && (
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Crown className="size-5 text-primary-glow shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Desbloqueie metas ilimitadas com o Premium</p>
              <p className="text-muted-foreground mt-0.5">
                O plano gratuito permite até {goalsLimit} metas. Com o Premium você cria <span className="text-foreground font-medium">quantas metas quiser</span>, sem limites — além de insights avançados e previsões financeiras.
              </p>
            </div>
          </div>
          <Link
            to="/assinatura"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-primary text-primary-foreground font-medium text-sm shadow-glow hover:opacity-90 transition-opacity shrink-0"
          >
            <Crown className="size-4" />
            Assinar Premium
          </Link>
        </div>
      )}

      {open && (
        <form
          onSubmit={handleAdd}
          className="rounded-3xl bg-gradient-card border border-border/40 p-6 grid sm:grid-cols-3 gap-3"
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome da meta"
            required
            className="px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none sm:col-span-3"
          />
          <input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="Valor alvo (R$)"
            required
            className="px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none"
          />
          <input
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="Valor atual (R$)"
            className="px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none"
          />
          <button
            type="submit"
            className="px-4 py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-medium shadow-glow"
          >
            Criar meta
          </button>
        </form>
      )}

      {goals.length === 0 ? (
        <div className="rounded-3xl bg-gradient-card border border-border/40 p-12 text-center">
          <Target className="size-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-display font-semibold">Nenhuma meta ainda</p>
          <p className="text-sm text-muted-foreground mt-1">Crie sua primeira meta para começar.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {goals.map((g) => {
            const pct = Math.min(100, (g.current / g.target) * 100);
            const remaining = Math.max(0, g.target - g.current);
            return (
              <div
                key={g.id}
                className="rounded-3xl bg-gradient-card border border-border/40 p-6 group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-display font-semibold text-lg">{g.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {pct >= 100 ? "🎉 Meta concluída!" : `Faltam ${formatBRL(remaining)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => removeGoal(g.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    aria-label="Remover meta"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>

                <div className="flex items-baseline justify-between mb-2">
                  <span className="font-display text-2xl font-bold">{formatBRL(g.current)}</span>
                  <span className="text-sm text-muted-foreground">de {formatBRL(g.target)}</span>
                </div>

                <div className="h-3 rounded-full bg-background/60 overflow-hidden">
                  <div
                    className="h-full bg-gradient-primary rounded-full transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-xs text-primary-glow font-medium mt-2">{pct.toFixed(0)}%</p>

                <div className="mt-4 space-y-2">
                  <label htmlFor={`goal-amount-${g.id}`} className="text-xs font-medium text-muted-foreground">
                    Movimentar valor:
                  </label>
                  <Input
                    id={`goal-amount-${g.id}`}
                    type="number"
                    inputMode="decimal"
                    min="0.01"
                    step="0.01"
                    value={goalAmounts[g.id] ?? ""}
                    onChange={(event) => setGoalAmounts((values) => ({ ...values, [g.id]: event.target.value }))}
                    placeholder="Valor (R$)"
                    aria-label={`Valor para movimentar na meta ${g.name}`}
                    className="h-11 rounded-xl bg-input"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="h-11 rounded-xl"
                      disabled={updatingGoalId === g.id}
                      onClick={() => handleGoalAmount(g.id, g.current, "remove")}
                    >
                      <Minus /> Retirar
                    </Button>
                    <Button
                      type="button"
                      className="h-11 rounded-xl"
                      disabled={updatingGoalId === g.id}
                      onClick={() => handleGoalAmount(g.id, g.current, "add")}
                    >
                      <Plus /> Adicionar
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
